import React, { useEffect, useState } from "react";
import { CheckCircle2, Download, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { canPreview } from "./companyDocumentUtils";

/**
 * The employee's confirmation. `doc` is live from the page's list, so opening the
 * document (or the file being replaced meanwhile) updates what is shown here.
 *
 * onOpenDocument views or downloads the file. onConfirm should throw on failure
 * so the dialog stays open.
 */
export default function CompanyDocumentAcknowledgeDialog({ doc, onClose, onOpenDocument, onConfirm }) {
  const [confirmed, setConfirmed] = useState(false);
  const [opening, setOpening] = useState(false);
  const [saving, setSaving] = useState(false);

  const opened = doc?.my_acknowledgement?.status === "opened";

  // A replaced file puts them back to "not opened": confirm that one afresh.
  useEffect(() => {
    setConfirmed(false);
  }, [doc?._id, opened]);

  const handleOpen = async () => {
    setOpening(true);
    try {
      await onOpenDocument(doc);
    } finally {
      setOpening(false);
    }
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onConfirm(doc);
    } catch {
      // The caller reports the error; stay open.
    } finally {
      setSaving(false);
    }
  };

  const preview = doc && canPreview(doc);
  const OpenIcon = preview ? Eye : Download;
  const openLabel = preview ? "Open document" : "Download document";

  return (
    <Dialog open={Boolean(doc)} onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pr-6 text-left">
          <DialogTitle>Acknowledge document</DialogTitle>
          <DialogDescription>
            Your company has asked you to confirm you have read this document.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-primary/15 bg-primary/[0.03] p-3 dark:border-primary/20 dark:bg-primary/[0.06]">
            <p className="break-words font-medium text-foreground">{doc?.title}</p>
            <p className="mt-0.5 break-words text-xs text-muted-foreground">{doc?.file_name}</p>
            <Button
              type="button"
              variant={opened ? "ghost" : "outline"}
              size="sm"
              className="mt-3"
              onClick={handleOpen}
              disabled={opening}
            >
              {opening ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <OpenIcon className="h-4 w-4" />
              )}
              {opened ? `${preview ? "Open" : "Download"} again` : openLabel}
            </Button>
            {opened && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                You have opened this document
              </p>
            )}
          </div>

          <div className="flex items-start gap-2.5">
            <Checkbox
              id="company-doc-acknowledge"
              className="mt-0.5"
              checked={confirmed}
              disabled={!opened}
              onCheckedChange={(value) => setConfirmed(value === true)}
            />
            <Label
              htmlFor="company-doc-acknowledge"
              className={`text-sm font-normal leading-snug ${opened ? "" : "text-muted-foreground"}`}
            >
              I have read and understood this document.
            </Label>
          </div>
          {!opened && (
            <p className="text-xs text-muted-foreground">Open the document first to continue.</p>
          )}
        </div>

        <DialogFooter className="flex-row gap-2 space-x-0 sm:space-x-0">
          <Button variant="outline" className="flex-1 sm:flex-none" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button className="flex-1 sm:flex-none" onClick={handleConfirm} disabled={!opened || !confirmed || saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Acknowledge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
